import { CenteredPage, urlUtil } from '@cmg/common';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import externalRouteFactory from '../../../common/util/externalRouteFactory';
import type { ActivateUser } from '../../../types/domain/self-settings/ActivateUser';
import FirstTimeActivationForm from './components/FirstTimeActivationForm';
import { activateUser, resetState, selectError, selectSubmitting, selectSuccess } from './ducks';

type QueryParams = {
  userId?: string;
  code?: string;
  firstName: string;
  lastName: string;
  email?: string;
};

/**
 * Displays a centered user activation form. This route is accessed from an activate
 * user account email.
 * If you come here without having the necessary info to activate, then it
 * redirects to login.
 */
export const FirstTimeActivationRouteComponent: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const error = useSelector(selectError);
  const success = useSelector(selectSuccess);
  const submitting = useSelector(selectSubmitting);

  const {
    userId,
    email,
    firstName,
    lastName,
    code: token,
  } = urlUtil.queryParse<QueryParams>(location.search);

  useEffect(() => {
    // Didn't come here directly from email, so kick em out
    if (!userId || !token || !email) {
      window.location.replace(externalRouteFactory.ecmSpa.getUrl());
    }

    return () => {
      dispatch(resetState());
    };
  }, [dispatch, email, token, userId]);

  const handleSubmit = useCallback(
    (values: ActivateUser) => {
      dispatch(activateUser(values));
    },
    [dispatch]
  );

  if (!userId || !token || !email) {
    return null;
  }

  return (
    <CenteredPage title="Activate Account">
      <FirstTimeActivationForm
        onSubmit={handleSubmit}
        error={error}
        success={success}
        submitting={submitting}
        token={token}
        userId={userId}
        firstName={firstName}
        lastName={lastName}
        email={email}
      />
    </CenteredPage>
  );
};

export default FirstTimeActivationRouteComponent;
